/**
 * A messaging attachment as a bubble draws it. Both inboxes build it the same way,
 * so a photo looks the same wherever the conversation is read.
 */

/** `unknown` = a URL of unlearned type; `none` = not a media row, whatever URL it carries. */
export type MediaKind = 'image' | 'video' | 'audio' | 'file' | 'unknown' | 'none'

interface MediaFacts {
  /** Private storage only: forces the original file name. Platform-CDN media has none. */
  downloadUrl: string | null
  fileName: string | null
  /** A bare story reply says nothing about why the media is there. */
  storyReply: boolean
}

/** Only a file can lack a URL: media that could not be kept or signed is drawn as its name. */
export type ThreadMedia =
  | (MediaFacts & { kind: 'file'; url: string | null })
  | (MediaFacts & { kind: Exclude<MediaKind, 'none' | 'file'>; url: string })

const MESSAGE_TYPE_MEDIA_KIND: Partial<Record<string, MediaKind>> = {
  image: 'image',
  story_reply: 'unknown',
  video: 'video',
  audio: 'audio',
  file: 'file',
}

/** Null when the row has nothing to draw. */
export function toThreadMedia(message: {
  messageType: string
  contentPayload: Record<string, unknown> | null
  url: string | null
  downloadUrl: string | null
}): ThreadMedia | null {
  const fileName =
    typeof message.contentPayload?.file_name === 'string' ? message.contentPayload.file_name : null
  const facts: MediaFacts = {
    downloadUrl: message.downloadUrl,
    fileName,
    storyReply: message.messageType === 'story_reply',
  }

  if (message.messageType === 'file') return { kind: 'file', url: message.url, ...facts }
  // Degraded media keeps its file name, so the inbound stays visible: the AI replies
  // to these, and a hidden bubble would orphan the agent's reply.
  if (!message.url) return fileName ? { kind: 'file', url: null, ...facts } : null

  const kind = resolveMediaKind(message.messageType, message.contentPayload)
  return kind === 'none' ? null : { kind, url: message.url, ...facts }
}

/** `mime_type` wins: `message_type` carries the semantic, not what the bytes are. */
export function resolveMediaKind(
  messageType: string,
  contentPayload: Record<string, unknown> | null,
): MediaKind {
  const mime = typeof contentPayload?.mime_type === 'string' ? contentPayload.mime_type : null
  if (mime?.startsWith('image/')) return 'image'
  if (mime?.startsWith('video/')) return 'video'
  if (mime?.startsWith('audio/')) return 'audio'
  if (mime) return 'file'
  return MESSAGE_TYPE_MEDIA_KIND[messageType] ?? 'none'
}
